import React, { useEffect, useState } from 'react';
import apiClient from '../../services/api'; // Adjust path
import { UserStats, ActivityHeatmapData } from '../../types'; // Adjust path
import StreakCard from '../../components/gamification/StreakCounter'; // Corrected import path
import TodaysHabitSnapshot from '../../components/gamification/TodaysHabitSnapshot';
import ActivityHeatmap from '../../components/gamification/ActivityHeatmap'; // Adjust path
import { Flame, CheckCircle } from 'lucide-react';

// Placeholder components (implement in later steps)
// const StreakCounter: React.FC<{ stats: UserStats | null }> = ({ stats }) => 
//   <div>Current Streak: {stats?.current_streak ?? 'N/A'}</div>;
// const ActivityHeatmap: React.FC<{ data: ActivityHeatmapData[] }> = ({ data }) => 
//   <div>Activity Heatmap Placeholder (Data count: {data.length})</div>;

const DashboardPage: React.FC = () => {
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [heatmapData, setHeatmapData] = useState<ActivityHeatmapData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [statsRes, heatmapRes] = await Promise.all([
          apiClient.get<{ user_stats: UserStats }>('/goals/stats/user/'), // Assume nested structure based on potential backend change
          apiClient.get<ActivityHeatmapData[]>('/goals/stats/activity-heatmap/')
        ]);
        
        // Adjust based on actual API response structure
        setUserStats(statsRes.data.user_stats || statsRes.data);
        setHeatmapData(heatmapRes.data);
        // --- DEBUG LOGGING START ---
        console.log("Fetched User Stats:", statsRes.data);
        console.log("UserStats state set to:", statsRes.data.user_stats || statsRes.data);
        console.log("Fetched Heatmap Data:", heatmapRes.data);
        // --- DEBUG LOGGING END ---

      } catch (err: any) {
        console.error("Failed to fetch dashboard data:", err);
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Your personal dashboard.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Task Streak Card */}
        <StreakCard 
          title="Task Streak"
          // TODO (Backend): API /goals/stats/user/ needs to calculate and return current_task_streak
          streak={userStats?.current_task_streak} // Use the dedicated task streak field
          description="Consecutive days with tasks completed."
          icon={Flame}
          iconColor="text-orange-500"
          isLoading={isLoading}
        />
        
        {/* Habit Streak Card */}
        <StreakCard 
          title="Habit Streak"
          streak={userStats?.current_habit_streak}
          description="Consecutive days meeting daily habit goals."
          icon={CheckCircle}
          iconColor="text-green-500"
          isLoading={isLoading}
        />

        {/* Today's Habit Snapshot */}
        <TodaysHabitSnapshot 
          // TODO (Backend): API /goals/stats/user/ needs to return daily_habits_completed_today and total_daily_habits_today
          completedCount={userStats?.daily_habits_completed_today}
          totalCount={userStats?.total_daily_habits_today}
          isLoading={isLoading}
        />
      </div>

      <ActivityHeatmap 
        data={heatmapData} 
        isLoading={isLoading} 
        error={error} 
      />
      
      {error && !isLoading && <p className="text-red-500 text-center mt-4">Error loading dashboard data: {error}</p>}
    </div>
  );
};

export default DashboardPage; 